import {useModal} from "../context/ModalContext";
import {useCopyClipboard, useMatchChain, useToast, useUserInfo, useWallet} from "./index";
import {Button, TokenSendList} from "components";
import {QRCode} from "react-qrcode";
import {useEffect, useMemo, useRef, useState} from "react";
import {
    Address,
    useMatchWalletAssetsProps,
    UserAssetToken,
    UserImportTokenType, UserTransactionNextPageParams, UserTransactionRecordType,
    UserTransactionType,
    WalletAssetMergeType
} from "../types";
import {getUserWalletTransactionsApi, userImportTokenApi} from "../api";
import {isSuccess} from "../api/request";
import {Field, Input} from "../ui";
import {Abi} from "abitype";
import {useAssetListQuery, useImportTokenListQuery, USER_IMPORT_TOKEN_LIST_KEY} from "./api/wallet";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {NATIVE_TOKEN_ADDRESS} from "../config";
import {erc20Abi, formatUnits} from "viem";
import useLocalStore from "../store/useLocalStore";
import useContractStore, {ContractInfo} from "../store/useContractStore";
import useTransactionStore from "../store/useTransactionStore";
import {FormattedMessage, useIntl} from "react-intl";
import ImportToken from "../components/ImportToken";

const ReceiveModal = () => {
    const chain = useMatchChain()
    const {address} = useWallet();
    const [copied, setCopied] = useCopyClipboard();
    const toast = useToast()
    const intl = useIntl()

    const onCopy = () => {
        setCopied(address)
        toast.success(intl.formatMessage({
            id: "copied"
        }))
    }

    const chainLink = chain.explorerLink("address/" + address)
    return <div className={`matchid-receive-modal`}>
        <div className={"matchid-receive-container"}>
            <div className={`matchid-receive-text`}><FormattedMessage id={"receiveQrcode"}/></div>
            <div className={`matchid-qr-container`}>
                <QRCode margin="0"
                        className={`matchid-qr-code`}
                        value={address}/>
            </div>
            <a href={chainLink} target="_blank"
               className={`matchid-receive-link`}>
                {address}
            </a>
        </div>
        <Button size={"lg"} onClick={onCopy} block highlight disabled={copied}><FormattedMessage
            id={"copyAddress"}/></Button>
    </div>

}
export default function useMatchWallet() {
    const modal = useModal()
    const intl = useIntl()
    const showReceiveModal = () => {
        modal.open({
            title: intl.formatMessage({
                id: "receive"
            }),
            content: ReceiveModal
        })
    }

    const showImportTokenModal = () => {
        modal.open({
            title: intl.formatMessage({
                id: "customizedToken"
            }),
            content: ImportToken
        })
    }

    const showSendListModal = () => {
        modal.open({
            title: intl.formatMessage({
                id: "send"
            }),
            content: TokenSendList
        })
    }

    return {
        showReceiveModal,
        showImportTokenModal,
        showSendListModal
    }
}

export function useMatchWalletRecords() {
    const [hasMore, setHasMore] = useState(true);
    const [items, setItems] = useState<UserTransactionType[]>([])
    const {chainId, publicClient} = useMatchChain()
    const {address} = useWallet()
    const hasMoreRef = useRef(hasMore)
    const nextPageParamsRef = useRef<UserTransactionNextPageParams | undefined>(undefined)
    const {contracts, setContracts} = useContractStore()

    const fetchMoreData = async () => {
        const chainIdStr = chainId ? chainId.toString() : ""

        if (!hasMoreRef.current || !chainIdStr) {
            return
        }
        const res = await getUserWalletTransactionsApi({
            chain_id: chainIdStr,
            ...nextPageParamsRef.current,
        })
        if (isSuccess(res)) {
            if (res.data && res.data.transactions && res.data.transactions.length > 0) {
                setItems(items.concat(res.data.transactions || []));
            }
            hasMoreRef.current = res.data && res.data.transactions && res.data.transactions.length >= 50
            setHasMore(hasMoreRef.current)
            nextPageParamsRef.current = res.data?.next_page_params
        }
    };
    const onInit = async () => {
        nextPageParamsRef.current = undefined
        setItems([])
        setHasMore(true)
        hasMoreRef.current = true
        fetchMoreData()
    }
    useEffect(() => {
        if (chainId && address) {
            onInit()
        }
    }, [chainId, address]);

    const {transactions, removeTransaction} = useTransactionStore()
    const getContractInfoList = async(contractList:string[])=>{
        const contractUnique = Array.from(new Set(contractList)).filter(item => {
            const contract = contracts[`${chainId}-${item.toLowerCase()}`]
            if (!contract) {
                return true
            }
            if (!contract.symbol && !contract.name) {
                return true
            }
            if (!contract.decimals) {
                return true
            }
            return false
        })
        const calls = []
        for (const contract of contractUnique) {
            calls.push({
                address: contract as `0x${string}`,
                abi: erc20Abi,
                functionName: "symbol",
                args: []
            })
            calls.push({
                address: contract as `0x${string}`,
                abi: erc20Abi,
                functionName: "decimals",
                args: []
            })
        }
        const results = await publicClient?.multicall({contracts: calls});
        if (results) {
            console.log('contract results', calls, results)
            const contractMap = []
            for (let i = 0; i < contractUnique.length; i++) {
                const info: ContractInfo = {}
                if (results[i * 2].status == 'success') {
                    info.symbol = results[i * 2].result as string
                }
                if (results[i * 2 + 1].status == 'success') {
                    info.decimals = results[i * 2 + 1].result as number
                }
                // if(results[i * 3 + 2].status == 'success'){
                //     info.name = results[i * 3 + 2].result as string
                // }

                contractMap.push({
                    chainId: chainId || 0,
                    contractAddress: contractUnique[i],
                    info
                })
            }
            setContracts(contractMap)
        }
    }
    const list = useMemo(() => {
        const localTransactions = transactions[`${chainId}-${address}`] || []
        const removeList = localTransactions.filter(n => items.findIndex(m => m.hash == n.hash) >= 0);
        removeList.forEach(item => {
            removeTransaction({
                chainId: chainId || 0,
                hash: item.hash,
                address: address as Address
            })
        })
        const saveList = localTransactions.filter(n => items.findIndex(m => m.hash == n.hash) < 0);
        const list = [
            ...saveList,
            ...items
        ].sort((a, b) => {
            const btimestamp = typeof b.timestamp === 'string' ? parseInt(b.timestamp) : b.timestamp
            const atimestamp = typeof a.timestamp === 'string' ? parseInt(a.timestamp) : a.timestamp
            return btimestamp - atimestamp
        })
        const contractList = list.filter(item => item.input.toLowerCase().substring(0, 10) == '0xa9059cbb')
            .map(item => item.to)
        getContractInfoList(contractList)
        return list
    }, [transactions, chainId, items, address])


    return {
        items: list,
        fetchMoreData,
        hasMore
    }
}


export function useMatchWalletAssets({
                                         importTokenOptions,
                                         assetListOptions
                                     }: useMatchWalletAssetsProps = {}) {
    const {chainId, chain} = useMatchChain();


    const importTokenQuery = useImportTokenListQuery({
        chainId: chainId || 0,
        ...importTokenOptions
    });

    const assetListQuery = useAssetListQuery({
        chainId: chainId || 0,
        ...assetListOptions
    });

    const mergedAssets = useMemo(() => {
        if (!assetListQuery.data && !importTokenQuery.data) return [];

        const assetList = (assetListQuery.data || []).map(asset => ({
            ...asset,
            isAuto: true
        }));

        const importTokens = (importTokenQuery.data || []).map(token => ({
            ...token,
            isCustom: true
        }));

        const assetMap = new Map();
        assetList.forEach(asset => {
            const key = `${asset.chain_id}-${asset.address.toLowerCase()}`;
            assetMap.set(key, asset);
        });

        importTokens.forEach(token => {
            const key = `${token.chain_id}-${token.address.toLowerCase()}`;
            if (assetMap.has(key)) {
                const existing = assetMap.get(key);
                assetMap.set(key, {
                    ...existing,
                    symbol: token.symbol,
                    decimals: token.decimals,
                    isCustom: true,
                    isAuto: existing.isAuto ?? false
                });
            } else {
                assetMap.set(key, token);
            }
        });

        const list = Array.from(assetMap.values()).sort((a, b) => {
            return (b.isAuto ? 1 : 0) - (a.isAuto ? 1 : 0);
        });
        //check native token exist
        const nativeToken = list.find(asset => asset.address.toLowerCase() === NATIVE_TOKEN_ADDRESS.toLowerCase());
        if (!nativeToken) {
            list.unshift({
                chain_id: chain?.id.toString() || '',
                address: NATIVE_TOKEN_ADDRESS,
                decimals: chain?.nativeCurrency.decimals||18,
                symbol: chain?.nativeCurrency.symbol,
                name: chain?.nativeCurrency.name,
                balance: 0,
                icon: chain?.nativeCurrency.icon
            });
        }
        return list
    }, [assetListQuery.data, importTokenQuery.data]);

    return {
        importTokenQuery,
        assetListQuery,
        mergedAssets
    };
}


const MULTICALL_LIMIT = 100;

export function useMatchWalletAssetList({
                                            list
                                        }: {
    list: WalletAssetMergeType[]
}) {
    const {publicClient, chain} = useMatchChain();
    const {address} = useWallet()
    const {assets, setAssets} = useLocalStore()

    // Filter out the native token
    const nativeToken = list.find(asset => asset.address.toLowerCase() === NATIVE_TOKEN_ADDRESS.toLowerCase()) || {
        chain_id: chain?.id || '',
        address: NATIVE_TOKEN_ADDRESS,
        decimals: 18,
        symbol: chain?.nativeCurrency.symbol,
        name: chain?.nativeCurrency.name,
        balance: 0,
    }

    // 1. Query the native token balance
    const nativeBalanceQuery = useQuery({
        queryKey: ['nativeBalance', nativeToken?.chain_id],
        queryFn: async () => {
            if (!nativeToken || !publicClient) return null;
            try {
                return await publicClient.getBalance({
                    address: address as `0x${string}`,
                });
            } catch (error) {
                console.error('Native Token Balance Error:', error);
                return null;
            }
        },
        enabled: !!nativeToken && !!publicClient,
        refetchInterval: 15000, // Refresh every 15 seconds
        retry: 3 // Retry up to 3 times if failed
    });

    // 2. Filter out ERC20 tokens that require multiCall queries
    const erc20Tokens = useMemo(() => list.filter(asset => asset.address !== NATIVE_TOKEN_ADDRESS), [list]);

    // 3. Execute multiCall to query ERC20 balances
    const erc20BalanceQuery = useQuery({
        queryKey: ['erc20Balances', erc20Tokens.map(token => token.address)],
        queryFn: async () => {
            if (!erc20Tokens.length || !publicClient) return [];

            const calls = erc20Tokens.map(token => (
                {
                    address: token.address as `0x${string}`,
                    abi: erc20Abi as Abi,
                    functionName: "balanceOf",
                    args: [address as `0x${string}`]
                }
            ));

            let balances = [];
            for (let i = 0; i < calls.length; i += MULTICALL_LIMIT) {
                const batchCalls = calls.slice(i, i + MULTICALL_LIMIT);
                try {
                    const results = await publicClient.multicall({contracts: batchCalls});
                    balances.push(...results);
                } catch (error) {
                    console.error('MultiCall Error:', error);
                }
            }

            return balances;
        },
        enabled: !!publicClient,
        refetchInterval: 15000, // Refresh every 15 seconds
        retry: 3
    });


    // 4. Compute the holding value for isAuto=true tokens
    const enrichedAssets = useMemo(() => {
        if (!list) return [];

        const erc20Balances = erc20BalanceQuery.data || [];
        return list.map(asset => {
            let balanceValue = "0";

            let balance = 0;
            const decimals = typeof asset.decimals === 'string' ? parseInt(asset.decimals) : asset.decimals;
            const assetAddress = asset.address.toLowerCase();
            // Native token balance
            if (assetAddress === NATIVE_TOKEN_ADDRESS) {
                balanceValue = nativeBalanceQuery.data?.toString() || "0";
                balance = nativeBalanceQuery.data ? Number(formatUnits(nativeBalanceQuery.data, decimals)) : 0;
            }
            // ERC20 balance
            else {
                const index = erc20Tokens.findIndex(t => t.address.toLowerCase() === assetAddress);
                if (index !== -1 && erc20Balances[index] && erc20Balances[index].status === 'success') {
                    balance = Number(formatUnits(erc20Balances[index].result as bigint, decimals));
                    balanceValue = (erc20Balances[index].result as bigint)?.toString() || "0";
                }
            }

            // Calculate the holding value
            const value = asset.isAuto ? (balance * (asset.price || 0)) : 0;

            return {...asset, balance, value, balanceValue};
        });
    }, [list, nativeBalanceQuery.data, erc20BalanceQuery.data]);

    // 5. Sorting rules:
    useEffect(() => {
        const list = enrichedAssets.sort((a, b) => {
            // ① Native token is always at the top
            if (a.address === NATIVE_TOKEN_ADDRESS) return -1;
            if (b.address === NATIVE_TOKEN_ADDRESS) return 1;

            // ② Tokens with isAuto=true and non-zero balance are sorted by holding value in descending order
            if (a.isAuto && b.isAuto) return b.value - a.value;
            if (a.isAuto) return -1;
            if (b.isAuto) return 1;

            // ③ Tokens with isCustom=true and non-zero balance retain their original order
            if (a.isCustom && !a.isAuto && a.balance > 0) return -1;
            if (b.isCustom && !b.isAuto && b.balance > 0) return 1;

            // ④ Other tokens retain their original order
            return 0;
        });
        setAssets(list)
    }, [enrichedAssets]);

    return {
        list: assets,
        isLoading: nativeBalanceQuery.isLoading || erc20BalanceQuery.isLoading,
        isFetched: nativeBalanceQuery.isFetched && erc20BalanceQuery.isFetched,
        nativeBalanceQuery,
        erc20BalanceQuery,
        nativeBalance: nativeBalanceQuery.data,
        erc20Balances: erc20BalanceQuery.data
    };
}
